import { eventPublicPath } from "./eventLinks";
import { classPublicHref, classSessionDateKey } from "./classLinks";

export const CLASS_PREVIEW = 5;
export const EVENT_PREVIEW = 5;

export function formatClock(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}

export function formatWhen(iso) {
  if (!iso) return "";
  const dayPart = new Date(iso).toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
  return `${dayPart} · ${formatClock(iso)}`;
}

export function eventHref(ev) {
  return `${ev.public_path || eventPublicPath(ev) || "/events"}${
    ev.occurrence_date ? `?occurrence_date=${ev.occurrence_date}` : ""
  }`;
}

/** Href for a public class listing (one row per class → next instance). */
export function classListingHref(listing) {
  const date = classSessionDateKey(
    listing.next_instance?.effective_datetime
      || listing.next_instance?.datetime
      || listing.effective_datetime,
  );
  return classPublicHref(
    {
      ...listing,
      slug: listing.slug || listing.class?.slug,
      public_path: listing.public_path || listing.class?.public_path,
    },
    { date },
  );
}

export function classListingMeta(listing, headerSchoolName) {
  const parts = [];
  if (listing.schedule_label) parts.push(listing.schedule_label);
  const schoolName = listing.school?.name;
  if (schoolName && schoolName !== headerSchoolName) parts.push(schoolName);
  const tags = [listing.style_tag || listing.class?.style_tag, listing.level_tag || listing.class?.level_tag]
    .filter(Boolean)
    .join(" · ");
  if (tags) parts.push(tags);
  return parts.join(" · ");
}

export function classListingTitle(listing) {
  return listing.name || listing.class?.name || "Class";
}

export function eventListingMeta(ev) {
  const when = formatWhen(ev.occurrence_at || ev.starts_at);
  const loc = ev.location ? ` · ${ev.location}` : "";
  if (ev.recurring_label) {
    return when ? `${ev.recurring_label} · next ${when}${loc}` : `${ev.recurring_label}${loc}`;
  }
  return `${when}${loc}`;
}

export function nextEventSubtitle(ev) {
  const when = formatWhen(ev.occurrence_at || ev.starts_at);
  const loc = ev.location ? ` · ${ev.location}` : "";
  if (ev.recurring_label) {
    return when ? `${ev.recurring_label} · next ${when}${loc}` : `${ev.recurring_label}${loc}`;
  }
  return `${when}${loc}`;
}

/** True when the occurrence falls on the viewer's local calendar day. */
export function isSameLocalDay(iso, now = new Date()) {
  if (!iso) return false;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}

export function isTonight(ev, now = new Date()) {
  return isSameLocalDay(ev?.occurrence_at || ev?.starts_at, now);
}

/**
 * Strip relative-time / demo suffixes from series names so listing heroes
 * speak occurrence truth ("Salsa Social") instead of "3 Weeks Ago".
 */
const RELATIVE_TITLE_SUFFIX =
  /\s*[—–-]\s*(?:Live\s+Tonight|Tonight|\d+\s+Weeks?\s+Ago|Last\s+Week|Next\s+Week|This\s+Week)\s*$/i;

export function cleanEventDisplayTitle(name) {
  const raw = String(name || "").trim();
  if (!raw) return "Event";
  const cleaned = raw.replace(RELATIVE_TITLE_SUFFIX, "").trim();
  return cleaned || raw;
}

export function hostEventDisplayTitle(ev) {
  return cleanEventDisplayTitle(ev?.name);
}

export function sortEventsByOccurrence(events) {
  return [...(events || [])].sort((a, b) =>
    String(a.occurrence_at || a.starts_at || "").localeCompare(
      String(b.occurrence_at || b.starts_at || ""),
    ),
  );
}

export function isUpcomingOccurrence(iso) {
  if (!iso) return false;
  const start = new Date(iso);
  return !Number.isNaN(start.getTime()) && start.getTime() >= Date.now();
}

export function upcomingEventsOnly(list) {
  return (list || []).filter((ev) =>
    isUpcomingOccurrence(ev.occurrence_at || ev.starts_at),
  );
}

/** Prefer API-normalized listings; fall back for older calendar payloads. */
export function normalizeListingEvents(list) {
  return upcomingEventsOnly(
    (list || []).map((ev) => ({
      ...ev,
      occurrence_at: ev.occurrence_at || ev.starts_at,
    })),
  );
}

export function eventOccurrenceKey(ev) {
  return `${ev.id}:${ev.occurrence_date || ev.occurrence_at || ev.starts_at || ""}`;
}

/**
 * @param {object[]} events
 * @param {{ preferTonight?: boolean, now?: Date }} [options]
 *   preferTonight — when true, a same-day occurrence leads even if another
 *   series sorts earlier (host flyer only; school keeps default).
 */
export function splitNextEvent(events, { preferTonight = false, now = new Date() } = {}) {
  const sorted = sortEventsByOccurrence(events);
  if (preferTonight) {
    const tonightIdx = sorted.findIndex((ev) => isTonight(ev, now));
    if (tonightIdx > 0) {
      const [tonight] = sorted.splice(tonightIdx, 1);
      sorted.unshift(tonight);
    }
  }
  const nextEvent = sorted[0] || null;
  const listedEvents = nextEvent
    ? sorted.filter((ev) => eventOccurrenceKey(ev) !== eventOccurrenceKey(nextEvent))
    : sorted;
  return { nextEvent, listedEvents };
}
